import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  MessageSquare,
  Trash2,
  AlertTriangle,
  RefreshCw,
  Bot,
  X,
  Plus,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import CaseContextSummary from './CaseContextSummary';
import aiChatService from '../../services/aiChatService';
import useNotification from '../../hooks/useNotification';
import Notification from '../common/Notification';
import ConfirmDialog from '../common/ConfirmDialog';

const AIChatInterface = ({ caseId, isOpen, onClose }) => {
  const { notification, showSuccess, hideNotification } = useNotification();
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState('');
  const [error, setError] = useState(null);
  const [aiStatus, setAiStatus] = useState(null);
  const [caseContext, setCaseContext] = useState(null);
  const [showContextDetails, setShowContextDetails] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showNewSessionConfirm, setShowNewSessionConfirm] = useState(false);

  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const accumulatedMessageRef = useRef('');

  // Scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingMessage]);

  const loadInitialData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Load AI status, case context, and chat history in parallel
      const [statusResult, contextResult, historyResult] = await Promise.all([
        aiChatService.getStatus(),
        aiChatService.getCaseContext(caseId),
        aiChatService.getChatHistory(caseId),
      ]);

      setAiStatus(statusResult);
      setCaseContext(contextResult);
      setMessages(historyResult.messages || []);
    } catch (err) {
      console.error('Error loading chat data:', err);
      setError('Failed to load chat. Please try refreshing the page.');
    } finally {
      setIsLoading(false);
    }
  }, [caseId]);

  // Load initial data when chat opens
  useEffect(() => {
    if (isOpen && caseId) {
      loadInitialData();
    }
  }, [isOpen, caseId, loadInitialData]);

  const handleSendMessage = async (content) => {
    if (!aiStatus?.available) {
      setError('AI chat is not available. Please check your configuration.');
      return;
    }

    // Add user message immediately
    const userMessage = {
      id: Date.now(),
      role: 'user',
      content,
      created_at: new Date().toISOString(),
    };

    setMessages(prev => [...prev, userMessage]);
    setError(null);
    setIsStreaming(true);
    setStreamingMessage('');
    accumulatedMessageRef.current = '';

    try {
      await aiChatService.sendMessage(
        caseId,
        content,
        // onChunk
        (chunk) => {
          console.log('React onChunk called with:', chunk);
          accumulatedMessageRef.current += chunk;
          setStreamingMessage(accumulatedMessageRef.current);
          console.log('Updated accumulatedMessage to:', accumulatedMessageRef.current);
        },
        // onError
        (error) => {
          console.error('Streaming error:', error);
          setError(error.message || 'Failed to get AI response');
          setIsStreaming(false);
          setStreamingMessage('');
        },
        // onComplete
        () => {
          // Add complete assistant message using the accumulated content
          const assistantMessage = {
            id: Date.now() + 1,
            role: 'assistant',
            content: accumulatedMessageRef.current,
            created_at: new Date().toISOString(),
            ai_provider: aiStatus.current_provider,
          };

          setMessages(prev => [...prev, assistantMessage]);
          setIsStreaming(false);
          setStreamingMessage('');
          accumulatedMessageRef.current = '';
        }
      );
    } catch (err) {
      console.error('Error sending message:', err);
      setError(err.message || 'Failed to send message');
      setIsStreaming(false);
      setStreamingMessage('');
      accumulatedMessageRef.current = '';
    }
  };

  const handleCancelStreaming = () => {
    aiChatService.cancelRequest();
    setIsStreaming(false);
    setStreamingMessage('');
    accumulatedMessageRef.current = '';
  };

  const handleClearHistory = () => {
    setShowClearConfirm(true);
  };

  const confirmClearHistory = async () => {
    try {
      await aiChatService.clearChatHistory(caseId);
      setMessages([]);
      showSuccess('Chat history cleared successfully');
    } catch (err) {
      console.error('Error clearing history:', err);
      setError('Failed to clear chat history');
    } finally {
      setShowClearConfirm(false);
    }
  };

  const handleNewSession = () => {
    if (messages.length > 0) {
      setShowNewSessionConfirm(true);
    } else {
      // If no messages, just clear without confirmation
      setMessages([]);
      setStreamingMessage('');
      setError(null);
    }
  };

  const confirmNewSession = () => {
    setMessages([]);
    setStreamingMessage('');
    setError(null);
    setShowNewSessionConfirm(false);
  };

  const handleRefreshContext = async () => {
    try {
      await aiChatService.refreshCaseContext(caseId);
      const contextResult = await aiChatService.getCaseContext(caseId);
      setCaseContext(contextResult);
    } catch (err) {
      console.error('Error refreshing context:', err);
      setError('Failed to refresh case context');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-white z-[9999] flex flex-col">
      <div className="bg-white h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gray-50">
          <div className="flex items-center gap-3">
            <Bot className="text-yellow-600" size={24} />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                AI Case Assistant
              </h2>
              <p className="text-sm text-gray-600">
                {caseContext?.case_title || 'Loading case information...'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleNewSession}
              className="px-3 py-1 text-sm text-yellow-600 hover:text-gray-800 border border-blue-300 rounded hover:bg-gray-50 transition-colors flex items-center gap-1"
            >
              <Plus size={14} />
              New Session
            </button>

            {messages.length > 0 && (
              <button
                onClick={handleClearHistory}
                className="px-3 py-1 text-sm text-red-600 hover:text-red-800 border border-red-300 rounded hover:bg-red-50 transition-colors flex items-center gap-1"
              >
                <Trash2 size={14} />
                Clear All History
              </button>
            )}

            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="bg-red-50 border-b border-red-200 p-3 flex items-center gap-2">
            <AlertTriangle size={16} className="text-red-600 flex-shrink-0" />
            <span className="text-red-800 text-sm">{error}</span>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-600 hover:text-red-800"
            >
              <X size={16} />
            </button>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {isLoading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="flex items-center gap-3 text-gray-600">
                <RefreshCw size={20} className="animate-spin" />
                <span>Loading chat...</span>
              </div>
            </div>
          ) : (
            <>
              {/* Context Summary - Collapsible */}
              {caseContext && (
                <div className="border-b">
                  <div className="p-3 bg-gray-50">
                    <button
                      onClick={() => setShowContextDetails(!showContextDetails)}
                      className="flex items-center justify-between w-full text-left"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-700">
                          Case Context & Available Data
                        </span>
                        <span className="text-xs text-gray-500">
                          ({caseContext.documents || 0} documents, {caseContext.projects || 0} projects)
                        </span>
                      </div>
                      {showContextDetails ? (
                        <ChevronUp size={16} className="text-gray-500" />
                      ) : (
                        <ChevronDown size={16} className="text-gray-500" />
                      )}
                    </button>
                  </div>
                  {showContextDetails && (
                    <div className="p-4 bg-white">
                      <CaseContextSummary
                        context={caseContext}
                        onRefresh={handleRefreshContext}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Messages */}
              <div
                ref={chatContainerRef}
                className="flex-1 overflow-y-auto"
              >
                {messages.length === 0 && !isStreaming ? (
                  <div className="flex-1 flex items-center justify-center p-8">
                    <div className="text-center text-gray-500">
                      <MessageSquare size={48} className="mx-auto mb-4 text-gray-400" />
                      <h3 className="text-lg font-medium mb-2">
                        Start a conversation about this case
                      </h3>
                      <p className="text-sm max-w-md">
                        Ask questions about case details, documents, tasks, timeline,
                        or anything related to this matter. The AI has full context
                        of your case information.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {messages.map((message) => (
                      <ChatMessage key={message.id} message={message} />
                    ))}

                    {/* Streaming message */}
                    {isStreaming && (
                      <ChatMessage
                        message={{
                          id: 'streaming',
                          role: 'assistant',
                          content: streamingMessage,
                          created_at: new Date().toISOString(),
                        }}
                        isStreaming={true}
                      />
                    )}

                    <div ref={messagesEndRef} />
                  </div>
                )}
              </div>

              {/* Input */}
              <ChatInput
                onSend={handleSendMessage}
                disabled={!aiStatus?.available}
                isStreaming={isStreaming}
                onCancel={handleCancelStreaming}
              />
            </>
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={showClearConfirm}
        onClose={() => setShowClearConfirm(false)}
        onConfirm={confirmClearHistory}
        title="Clear Chat History"
        message="Are you sure you want to clear all chat history? This action cannot be undone."
        confirmText="Clear History"
        cancelText="Cancel"
        variant="danger"
      />

      <ConfirmDialog
        isOpen={showNewSessionConfirm}
        onClose={() => setShowNewSessionConfirm(false)}
        onConfirm={confirmNewSession}
        title="Start New Session"
        message="Start a new session? This will clear the current conversation."
        confirmText="Start New Session"
        cancelText="Cancel"
        variant="warning"
      />

      <Notification
        isOpen={notification.isOpen}
        onClose={hideNotification}
        title={notification.title}
        message={notification.message}
        variant={notification.variant}
        duration={notification.duration}
      />
    </div>
  );
};

export default AIChatInterface;
